<template>
  <article class="workspacefolder-view">
    <template v-if="currentFolder && workspace">
      <header class="workspacefolder-view__header">
        <h1 class="workspacefolder-view__heading">{{ currentFolder.name }}</h1>

        <div class="workspacefolder-view__buttonbar">
          <button
            class="button button_large"
            type="button"
            @click="openCreatePopup()"
          >
            {{ $t("labels.newModel") }}
          </button>
          <button
            class="button button_secondary"
            type="button"
            @click="openImportPopup()"
          >
            {{ $t("labels.importModel") }}
          </button>

          <button
            class="button-icon pr-0 mr-2"
            type="button"
            @click="state.listOptionsVisible = true"
          >
            <lucide-icon name="settings-2" class="text-24" />
          </button>
        </div>
      </header>

      <div class="line_flex -between pv-2">
        <button
          class="workspacefolder-view__loading"
          :title="$t('actions.updateNow')"
          type="button"
          @click="
            loadModels({
              loadAll: false,
              synchErrorsMuted: false
            })
          "
        >
          <span class="workspacefolder-view__loadingstatus">
            <span v-if="state.loading">{{ $t("messages.loadingData") }}</span>
            <span v-else-if="!state.lastUpdated">
              {{ $t("actions.reloadModels") }}
            </span>
            <span v-else>
              {{ $t("labels.lastUpdatedXAgo", [lastUpdatedFormatted]) }}
            </span>
          </span>

          <lucide-icon
            v-show="!state.loading"
            name="refresh-cw"
            class="workspacefolder-view__reloadicon"
          />
        </button>
      </div>

      <div class="mt-4" v-if="folderModels.length > 0">
        <div class="column gap-4 column--container">
          <workspace-model-entry
            v-for="model in orderedFolderModels"
            :key="model.id"
            draggable="true"
            :model="model"
            :workspace="workspace"
            :info-view-route-name="
              workspace?.isPersonal
                ? 'personal-workspace-model-info'
                : 'team-model-info'
            "
            :editor-view-route-name="editorViewRouteName"
            :description-visible="modelDescriptionsVisible"
            @dragstart="onModelDragStart($event, model)"
          ></workspace-model-entry>
        </div>
      </div>
      <template v-else-if="!state.loading">
        <error-page-content
          v-if="state.loadingError"
          :error-code="state.loadingError.code"
          :status="state.loadingError.status"
        />

        <template v-else>
          <div v-if="totalModelsAmount == 0 && workspace.isPersonal" class="workspacefolder-view__welcomemessage">
            <lucide-icon name="hand" class="workspacefolder-view__welcomemessage__hand" />
            <h2>{{ $t("headings.firstWelcome") }}</h2>
            <p>{{ $t("texts.startWithAModel") }}</p>
          </div>
          <div v-else class="message message-info mt-4">
            {{ $t("messages.noModelsFound") }}
          </div>
        </template>
      </template>

      <div v-if="license.nonExpiredLicenses.length == 0" class="workspacefolder-view__free-notice">
        
        <span v-if="license.limits.maxModels != null" class="workspacefolder-view__free-notice__limit">
          {{ totalModelsAmount }} / {{ license.limits.maxModels }}
          {{ $t("objectNames.model", license.limits.maxModels) }}
          <lucide-icon name="lock" />
        </span>
        <i18n-t
          tag="p"
          keypath="texts.usingPlan"
          scope="global"
        >
          <template #planname>
            <strong>simcision Free</strong>
          </template>
        </i18n-t>
        <p>{{ $t("texts.subscriptionTeaser") }}</p>
        <router-link :to="{ name: 'order-subscription' }" class="button mt-2">
          {{ $t("actions.buySubscription") }}
        </router-link>
      </div>

      <model-create-popup
        :visible="state.createPopupVisible"
        :organization-slug="organizationSlug"
        :team-slug="teamSlug"
        :folder="currentFolder"
        @create="onModelCreated($event)"
        @close="state.createPopupVisible = false"
      />

      <model-import-popup
        v-if="workspace"
        :visible="state.importingModel"
        :workspace-id="workspace.id"
        :folder-id="currentFolder.id"
        @close="state.importingModel = false"
      />

      <model-list-options
        v-model:order-by="orderModelsBy"
        v-model:show-descriptions="modelDescriptionsVisible"
        :visible="state.listOptionsVisible"
        @close="state.listOptionsVisible = false"
      />
    </template>
    <div v-else>
      <error-page-content
        error-code="not_found"
        :title="
          $t('messages.folderNotFound', {
            name: organizationSlug
              ? `${organizationSlug}/${folderSlug}`
              : folderSlug
          })
        "
        :status="404"
      />
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, watch, ref, reactive } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useIntervalFn } from "@vueuse/core";
import { DateTime } from "luxon";

import { formatDateRelative } from "@/i18n";
import { useNow } from "@core/composables/useNow";
import envOptions from "@/helpers/Env";

import { useWorkspaces } from "@/api/workspaces.store";
import { useModels } from "@/api/models.store";
import { useUser } from "@/api/user.store";
import { orderModels } from "@/api/modelFunctions";
import { useLicense } from "@/api/license.store";

import LucideIcon from "@core/ui/LucideIcon.vue";
import ErrorPageContent from "@core/errors/ErrorPageContent.vue";

import WorkspaceModelEntry from "@manager/workspacemodels/WorkspaceModelEntry.vue";
import ModelImportPopup from "@manager/workspacemodels/ModelImportPopup.vue";
import ModelCreatePopup from "@manager/workspacemodels/ModelCreatePopup.vue";
import ModelListOptions from "@manager/workspacemodels/ModelListOptions.vue";

import type { Model } from "@/api/Model";

const props = defineProps<{
  folderSlug: string;
  organizationSlug?: string;
  teamSlug?: string;
}>();

const { t } = useI18n();
const router = useRouter();

const { user } = useUser();
const license = useLicense();
const { currentWorkspace, currentFolder } = useWorkspaces(props);
const { getFolderModels, loadAllModels, getModelsFilteredByFolder, models } =
  useModels();

const orderModelsBy = ref(user.value?.preferredModelOrder ?? "modified");
const modelDescriptionsVisible = ref(user.value?.showModelDetails ?? true);

const workspace = currentWorkspace;
const now = useNow();

const state = reactive({
  loading: false,
  loadingError: null as null | (Error & { status?: number; code?: string }),
  newModelName: "",
  createPopupVisible: false,
  lastUpdated: 0,
  importingModel: false,
  listOptionsVisible: false
});

// Reload the folder models every 5 minutes
const FIVE_MINUTES = 5 * 60 * 1000;
useIntervalFn(() => {
  loadModels({
    loadAll: false,
    synchErrorsMuted: true
  });
}, FIVE_MINUTES);

watch(() => user.value?.id, (id, oldId) => {
  if (id != oldId) {
    loadModels({
      loadAll: true,
      synchErrorsMuted: true
    })
  }
});

watch(
  () => props.folderSlug,
  (value, oldValue) => {
    if (value == oldValue) {
      return;
    }
    const loadAll = envOptions.saveLocally && oldValue == null;
    loadModels({
      loadAll: loadAll,
      synchErrorsMuted: true
    });
  },
  { immediate: true }
);

const folderModels = computed(() => {
  if (currentFolder.value == null) return [];
  return getModelsFilteredByFolder(currentFolder.value.id);
});

const orderedFolderModels = computed(() => {
  return orderModels(folderModels.value, orderModelsBy.value);
});

const lastUpdatedFormatted = computed(() => {
  const nowDate = new Date(now.value);
  const date = new Date(state.lastUpdated);
  const earlierTime = nowDate.getTime() < date.getTime() ? nowDate : date;
  const dateTime = DateTime.fromJSDate(earlierTime);
  const minutesSinceUpdate = Math.abs(
    dateTime.diffNow("minutes").as("minutes")
  );
  if (minutesSinceUpdate < 1) {
    return t("messages.lessThenAMinuteAgo");
  }
  return formatDateRelative(earlierTime);
});

const isPersonalWorkspace = computed(() => {
  return workspace.value?.isPersonal;
});

const editorViewRouteName = computed(() => {
  return isPersonalWorkspace.value ? "personal-workspace-model" : "team-model";
});

async function loadModels({
  loadAll = true,
  synchErrorsMuted = true
}) {
  if (state.loading) {
    return;
  }
  if (!workspace.value) return;

  state.loading = true;
  state.loadingError = null;

  try {
    if (loadAll) {
      // Synchronize ALL models
      await loadAllModels({ waitForSync: true, synchErrorsMuted });
    } else {
      // Get the local folder only
      await getFolderModels(workspace.value, props.folderSlug, {
        synchErrorsMuted,
        waitForSync: true
      });
    }

    state.lastUpdated = Date.now();
    console.log("updated", state.lastUpdated);
  } catch (error) {
    state.loadingError = error as Error;

    // TODO: handle connection errors differently?
  } finally {
    state.loading = false;
  }
}

function openCreatePopup() {
  state.createPopupVisible = true;
}

function openImportPopup() {
  state.importingModel = true;
}

/**
 * Open the new model as soon as it was created
 */
function onModelCreated(newModel: { id: number | string }) {
  let linkParams;
  if (workspace.value?.isPersonal) {
    linkParams = {
      modelId: newModel.id,
      folderSlug: props.folderSlug
    };
  } else {
    linkParams = {
      modelId: newModel.id,
      organizationSlug: workspace.value?.organizationSlug,
      teamSlug: workspace.value?.slug,
      folderSlug: props.folderSlug
    };
  }

  const url = router.resolve({
    name: editorViewRouteName.value,
    params: linkParams
  });

  if (envOptions.openModelsInNewTab) {
    window.open(url.href, "_blank");
  } else {
    router.push({
      name: editorViewRouteName.value,
      params: linkParams
    });
  }
}

function onModelDragStart(dragEvent: DragEvent, model: Model) {
  if (dragEvent.dataTransfer == null) return;
  dragEvent.dataTransfer.setData("simcision/modelid", model.id.toString());
  dragEvent.dataTransfer.effectAllowed = "copyMove";
}

const totalModelsAmount = computed(() => Object.keys(models).length);
</script>

<style lang="scss">
.workspacefolder-view {
  width: 100%;
  max-width: 60rem;
  margin: 0 auto;

  &__header {
    margin-bottom: 1rem;
    display: grid;
    gap: 1rem;
    grid-template-columns: minmax(min-content, 1fr) minmax(0, max-content);
    align-items: center;
  }

  &__heading {
    margin-top: 0;
    margin-bottom: 0;
    min-width: 0;
    font-size: 2rem;
  }

  &__buttonbar {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-wrap: wrap;
    row-gap: 0.5rem;
    justify-content: flex-end;
  }

  &__loading {
    align-self: center;

    border: none;
    background: none;
    padding: 2px 0;
    padding-right: 4px;
    font-size: 13px;

    color: var(--color-text-light);

    &:focus-visible {
      color: var(--color-action-dark);
      outline: none;
      border-color: var(--color-action);
      box-shadow: 0 0 0 1px var(--color-action);
    }
  }
  &__loadingstatus {
    align-self: center;
  }
  &__reloadicon {
    display: inline-block;
    margin-left: 0.5rem;
    color: var(--color-text-light);
    transition:
      color 50ms,
      box-shadow 100ms;

    font-size: 1em;
  }

  &__loading:hover &__reloadicon {
    color: var(--color-action);
    text-shadow: 0 0 2px var(--color-shadow-2);
  }

  &__modellimit {
    font-size: 0.9rem;
    color: var(--color-text-medium);
    border: 1px solid var(--color-border);
    width: fit-content;
    padding: 0.25rem 0.5rem;
    background-color: var(--color-background-deep);
    border-radius: 0.25rem;
  }

  &__welcomemessage {
    padding: 1rem;
    padding-top: 2rem;
    text-align: center;
    font-size: 18px;

    &__hand {
      font-size: 3rem;
      color: var(--color-text-medium);
      animation: hand-wave 2s infinite;
      animation-timing-function: ease-in-out;
      transform-origin: 50% 100%;
    }
    h2 {
      margin-top: 0.5rem;
    }
  }

  &__free-notice {
    position: relative;
    margin-top: 6rem;
    align-items: center;
    border-radius: 2rem;
    padding: 3rem 2rem;
    padding-top: 4rem;
    background-color: var(--color-background-deep);
    text-align: center;
    font-size: 18px;

    & > p:first-of-type {
      margin-top: 0;
    }

    p {
      text-wrap: balance;
      max-width: 36em;
      margin-inline: auto;
    }

    button {
      margin-top: 0.5rem;
    }

    &__limit {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      font-size: 1rem;
      background-color: var(--color-background-shallow);
      padding: 1rem;
      border-radius: 1.5rem;

      .lucide-icon {
        color: var(--color-text-medium);
        margin-left: 0.25rem;
      }
    }
  }

  @keyframes hand-wave {
    0% {
      transform: rotate(10deg);
    }
    50% {
      transform: rotate(-10deg);
    }
    100% {
      transform: rotate(10deg);
    }
  }
}
</style>
